import React from 'react';
import { Box, Skeleton, Stack, Paper, TableRow, TableCell, Divider } from '@mui/material';

interface LoadingSkeletonProps {
  variant?: 'text' | 'rectangular' | 'circular';
  width?: number | string;
  height?: number | string;
  animation?: 'pulse' | 'wave' | false;
  sx?: any;
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'rectangular',
  width = '100%',
  height = 40,
  animation = 'wave',
  sx = {},
}) => {
  return (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation={animation}
      sx={{
        borderRadius: variant === 'rectangular' ? 2 : undefined,
        ...sx,
      }}
    />
  );
};

// Project Card Skeleton
export const ProjectCardSkeleton: React.FC = () => {
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        minHeight: '280px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <LoadingSkeleton width="60%" height={24} />
          <LoadingSkeleton width={60} height={24} />
        </Box>
        
        {/* Description */}
        <LoadingSkeleton width="100%" height={16} />
        <LoadingSkeleton width="80%" height={16} />
        
        {/* Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
          <Paper sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <LoadingSkeleton width="100%" height={20} />
            <LoadingSkeleton width="60%" height={14} sx={{ mt: 0.5 }} />
          </Paper>
          <Paper sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <LoadingSkeleton width="100%" height={20} />
            <LoadingSkeleton width="60%" height={14} sx={{ mt: 0.5 }} />
          </Paper>
        </Box>
        
        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingSkeleton width={60} height={24} />
            <LoadingSkeleton width={80} height={24} />
          </Box>
          <LoadingSkeleton width={80} height={16} />
        </Box>
      </Stack>
    </Paper>
  );
};

// Document Card Skeleton
export const DocumentCardSkeleton: React.FC = () => {
  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LoadingSkeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <LoadingSkeleton width="70%" height={18} />
            <LoadingSkeleton width="50%" height={14} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        <LoadingSkeleton width="100%" height={14} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <LoadingSkeleton width={80} height={20} />
          <LoadingSkeleton width={60} height={14} />
        </Box>
      </Stack>
    </Paper>
  );
};

// Deal Card Skeleton
export const DealCardSkeleton: React.FC = () => {
  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Box sx={{ flex: 1 }}>
            <LoadingSkeleton width="80%" height={24} />
            <LoadingSkeleton width="60%" height={16} sx={{ mt: 1 }} />
          </Box>
          <LoadingSkeleton width={80} height={32} />
        </Box>
        
        <LoadingSkeleton width="100%" height={16} />
        <LoadingSkeleton width="90%" height={16} />
        
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <Box>
            <LoadingSkeleton width="100%" height={20} />
            <LoadingSkeleton width="60%" height={14} sx={{ mt: 0.5 }} />
          </Box>
          <Box>
            <LoadingSkeleton width="100%" height={20} />
            <LoadingSkeleton width="60%" height={14} sx={{ mt: 0.5 }} />
          </Box>
          <Box>
            <LoadingSkeleton width="100%" height={20} />
            <LoadingSkeleton width="60%" height={14} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <LoadingSkeleton width={60} height={24} />
          <LoadingSkeleton width={80} height={24} />
          <LoadingSkeleton width={70} height={24} />
        </Box>
      </Stack>
    </Paper>
  );
};

// Chat Message Skeleton
export const ChatMessageSkeleton: React.FC<{ isUser?: boolean }> = ({ isUser = false }) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      mb: 2 
    }}>
      <Paper sx={{ 
        p: 2, 
        maxWidth: '75%',
        bgcolor: isUser ? 'primary.main' : 'background.paper',
        borderRadius: 2
      }}>
        <Stack spacing={1}>
          <LoadingSkeleton width="100%" height={16} />
          <LoadingSkeleton width="80%" height={16} />
          <LoadingSkeleton width="60%" height={16} />
        </Stack>
      </Paper>
    </Box>
  );
};

// Table Row Skeleton for MUI Table
export const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 4 }) => {
  return (
    <TableRow>
      {Array.from({ length: columns }).map((_, index) => (
        <TableCell key={index}>
          <LoadingSkeleton width="80%" height={16} />
        </TableCell>
      ))}
    </TableRow>
  );
};

// Page Header Skeleton
export const PageHeaderSkeleton: React.FC = () => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <LoadingSkeleton width={300} height={32} />
          <LoadingSkeleton width={200} height={16} sx={{ mt: 1 }} />
        </Box>
        <LoadingSkeleton width={120} height={40} />
      </Box>
      
      {/* Search and filters */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <LoadingSkeleton width={300} height={40} />
          <LoadingSkeleton width={40} height={40} />
          <LoadingSkeleton width={100} height={40} />
          <LoadingSkeleton width={100} height={40} />
        </Box>
      </Paper>
    </Box>
  );
};

// Dashboard Metric Skeleton
export const MetricCardSkeleton: React.FC = () => {
  return (
    <Paper sx={{ p: 3, borderRadius: 2, minWidth: 200 }}>
      <Stack spacing={1}>
        <LoadingSkeleton width="80%" height={16} />
        <LoadingSkeleton width="60%" height={32} />
        <LoadingSkeleton width="100%" height={14} />
      </Stack>
    </Paper>
  );
};

// Integration Card Skeleton
export const IntegrationCardSkeleton: React.FC = () => {
  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LoadingSkeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: 1 }} />
        <Box sx={{ flex: 1 }}>
          <LoadingSkeleton width="70%" height={24} />
          <LoadingSkeleton width="40%" height={20} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
      <LoadingSkeleton width="100%" height={40} />
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <LoadingSkeleton width={100} height={36} />
      </Box>
    </Paper>
  );
};

export default LoadingSkeleton;
